import React, { useEffect, useMemo, useState } from 'react';

import { formatDate } from '@betterment/core';
import { useGraphQLQuery } from '@betterment/graphql';
import { Box, Card, H2, Separator } from '@betterment/samba-react';
import { CursorProvider } from '@betterment/shared-components';
import { useDebounce } from 'use-debounce';

import { AccountsFilter } from './account-filter/AccountsFilter';
import { BalanceDetailsSheet } from './balance-details/BalanceDetailsSheet';
import { BalanceChart } from './BalanceChart';
import { BalanceChartData } from './BalanceChartData';
import { UserBalancePerformanceDocument } from './graphql/userPerformanceBalance.generated';

import { trackClick } from '../../../../features/track';
import { DateRangeOption } from '../../../../graphql/schema';
import { PerformanceAccountsQuery } from '../../graphql/performanceAccounts.generated';
import { PerformanceDisclosure } from '../performance-disclosure/PerformanceDisclosure';

export const Balance: React.FC<{
  dateRangeOption: DateRangeOption;
  accounts: PerformanceAccountsQuery['performance']['accounts'];
  initialSelectedIds?: string[];
}> = ({ dateRangeOption, accounts, initialSelectedIds = [] }) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set(initialSelectedIds));
  const [debounceTimer, setDebounceTimer] = useState(0);

  const handleBalanceDisclosureOpen = (open: boolean) => {
    if (open) {
      trackClick({
        name: 'balanceDisclosure',
        module: 'Performance',
        location: 'Balance',
      });
    }
  };

  useEffect(() => {
    // Set debounce timer after initial render to avoid delaying query fired on mount
    setDebounceTimer(1000);
  }, []);

  const [debouncedIds] = useDebounce(selectedIds, debounceTimer);
  const [debouncedDateRange] = useDebounce(dateRangeOption, debounceTimer);

  // Memoize array conversion to avoid unnecessary query re-renders
  const debouncedIdsArray = useMemo(() => Array.from(debouncedIds), [debouncedIds]);

  const { data, loading } = useGraphQLQuery(UserBalancePerformanceDocument, {
    variables: { dateRangeOption: debouncedDateRange, accountIds: debouncedIdsArray },
    throwOnError: true,
    /*
      Using no-cache here since this query and the one used in the sibling AccountsCard component have cache collisions
      Both update "performance" when the user has only selected one id on the account filer, however they request different attributes
      The mismatch in shape causes the GQL hooks to re-fetch to fix what it thinks is an improperly cached response
      Balance is expected to only show fresh data from the back-end, making requests as the user updates selected ids, while AccountsCard should only make its query on data init
      "no-cache" allows this query to not read or write to the cache, thus avoiding collisions with the AccountsCard query while maintaining expected behavior
    */
    fetchPolicy: 'no-cache',
  });

  let startDate: Date | undefined;
  let endDate: Date | undefined;

  if (data?.performance?.history) {
    const { history } = data.performance;
    if (history.timeSeries.length > 0) {
      startDate = new Date(history.timeSeries[0].date);
      endDate = new Date(history.timeSeries[history.timeSeries.length - 1].date);
    }
  }

  return (
    // Overflow is set to visible here to allow for the chart cursor label to show at the edges of the chart
    <Card css={{ overflow: 'visible' }}>
      <Card.Header css={{ padding: 'space.300', display: 'flex', justifyContent: 'space-between' }}>
        <H2 variant="title200">Balance</H2>
        <AccountsFilter accounts={accounts} selectedSetter={setSelectedIds} selectedAccountIds={selectedIds} />
      </Card.Header>
      <Separator css={{ fullWidthWithInset: 'space.300' }} />
      <Card.Content css={{ padding: 'space.300' }}>
        <CursorProvider>
          <BalanceChartData totals={data?.performance.totals} dateRangeOption={dateRangeOption} loading={loading} />
          <BalanceChart
            history={data?.performance.history}
            loading={loading}
            startingBalance={data?.performance.totals.startingBalance}
            endingBalance={data?.performance.totals.endingBalance}
            netDeposits={data?.performance.totals.userTransactions.netDeposits}
            totalEarned={data?.performance.totals.earned}
            adjustments={data?.performance.totals.investing?.adjustments}
          />
        </CursorProvider>
        {endDate && (
          <Box css={{ mt: 'space.200' }}>
            <PerformanceDisclosure
              asOf={`As of ${formatDate(endDate, 'longOrdinalSlashed')}.`}
              performanceDisclosure={data?.disclosures.performanceDisclosure || ''}
              onOpenChange={handleBalanceDisclosureOpen}
            />
          </Box>
        )}
        <Separator css={{ my: 'space.300' }} />
        <BalanceDetailsSheet
          data={data?.performance.totals}
          startDate={startDate}
          endDate={endDate}
          dateRangeOption={dateRangeOption}
        />
      </Card.Content>
    </Card>
  );
};
